<script lang="ts">
const __DsBlock = {
	type: 'input',
	icon: 'lucide:link',
	name: '$t:blocks.input_select_m2o.name',
	description: '$t:blocks.input_select_m2o.description',
	category: 'input',
	tags: ['form-control'],
	options: [
		{
			name: 'fieldId',
			label: '$t:blocks.common.props.field',
		},
		{
			name: 'referencedItemKey',
			label: '$t:blocks.common.props.referenced_item',
		},
		{
			name: 'disabled',
			label: '$t:blocks.common.props.disabled',
		},
		{
			name: 'readonly',
			label: '$t:blocks.common.props.readonly',
		},
		{
			name: 'disabledNotice',
			label: '$t:blocks.common.props.disabled_notice',
		},
	],

	hidden: ({ isRoot }) => isRoot,
}
</script>
<script lang="ts" setup>
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { CompositePrimaryKey, GlobalSingleRelationFieldId } from '#layers/data-model/public';
import type { RelationEditState } from '#layers/forms/public';
import { useOverlay } from '@nuxt/ui/composables';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppConfig } from '#app/config';
import { defineBlock } from '#layers/block-engine/public';
import M2ODeselectButton from '#layers/blocks/components/Relation/M2ODeselectButton.vue';
import RenderCollectionDisplayTemplate from '#layers/blocks/components/Render/RenderCollectionDisplayTemplate.vue';
import { useM2OSelection } from '#layers/blocks/composables/relation/use-m2o-selection';
import { useM2OState } from '#layers/blocks/composables/relation/use-m2o-state';
import { CollectionReads, getRelationReferenceKeyFields } from '#layers/data-model/public';
import { injectFormBlockContext, RelationEditKinds, useDrawerFieldOverrides, useM2OPermissions, useRelationFormField } from '#layers/forms/public';
import { LazyCollectionDetailDrawer } from '#layers/listing/lazy';
import { useDisabledNotice } from '#layers/ui/public';

interface InputSelectM2OBlockProps extends InputBlockProps {
	fieldId?: GlobalSingleRelationFieldId;
	referencedItemKey?: CompositePrimaryKey;
	showCreate?: boolean;
	showEdit?: boolean;
	editAllowed?: boolean;
	showRemove?: boolean;
}

const {
	showCreate = true,
	showEdit = true,
	editAllowed = true,
	showRemove = true,
	disabled: disabledProp,
	disabledNotice: disabledNoticeProp,
	fieldId,
} = defineProps<InputSelectM2OBlockProps>();

const { t } = useI18n();
const appConfig = useAppConfig();

const {
	field,
	referencedItemKey,
	emitConnect,
	emitDisconnect,
	emitCreate,
	emitUpdate,
	fieldValue,
	disabled,
	disabledNotice,
} = useRelationFormField<'to-one'>(() => ({ disabled: disabledProp, disabledNotice: disabledNoticeProp, fieldId }));
const fieldOverrides = useDrawerFieldOverrides(field, computed(() => field.value?.oppositeCollection ?? null));
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });

const referenceKeyFields = computed(() => field.value ? getRelationReferenceKeyFields(field.value) : []);
const { item, itemKey, effectiveKey, isCreate, isLoading } = useM2OState(field, referencedItemKey, fieldValue, {
	additionalSelection: () => ({
		fields: referenceKeyFields.value.map((referenceField) => referenceField.apiName),
	}),
});
const formBlockContext = injectFormBlockContext(null);

// Permission-based UI controls
const permissions = useM2OPermissions({
	field,
	writeContext: () => formBlockContext?.writeContext.value,
});

// A pending create rewrites its `_create`; any persisted item is edited through a nested `_update`.
const editState = computed<RelationEditState | undefined>(() => {
	if (isCreate.value) return { kind: RelationEditKinds.Created };
	return itemKey.value ? { kind: RelationEditKinds.Persisted, key: itemKey.value } : undefined;
});
const editContext = computed(() => editState.value && permissions.editContext(editState.value));
const canEditItem = computed(() => editAllowed && editContext.value !== undefined);

;

const overlay = useOverlay();

const { handleSelect, handleDeselect } = useM2OSelection({
	field,
	referenceKeyFields,
	effectiveKey,
	isCreate,
	referencedItemKey,
	emitConnect,
	emitDisconnect,
});

async function handleCreate() {
	const writeContext = permissions.createContext.value;
	if (!writeContext) return;
	const modal = overlay.create(LazyCollectionDetailDrawer, {
		destroyOnClose: true,
		props: {
			collectionId: field.value!.oppositeCollectionId,
			title: t('common.create_item'),
			formMode: 'emit',
			writeContext,
			fieldOverrides: fieldOverrides.value,
		},
	});

	const instance = modal.open();
	const payload = await instance.result;

	if (payload) {
		emitCreate(payload.data);
	}
}

async function handleEdit() {
	const state = editState.value;
	const writeContext = editContext.value;
	if (!state || !writeContext) return;
	const modal = useOverlay().create(LazyCollectionDetailDrawer, {
		destroyOnClose: true,
		props: {
			collectionId: field.value!.oppositeCollectionId,
			itemId: state.kind === RelationEditKinds.Created ? undefined : state.key,
			itemEdits: item.value ?? undefined,
			title: t('common.edit_item'),
			formMode: 'emit',
			writeContext,
			fieldOverrides: fieldOverrides.value,
		},
	});
	const instance = modal.open();
	const payload = await instance.result;

	if (payload) {
		if (state.kind === RelationEditKinds.Created) {
			emitCreate(payload.data);
		}
		else {
			emitUpdate(state.key, payload.data);
		}
	}
}

function handleView() {
	if (!itemKey.value) return;
	const modal = useOverlay().create(LazyCollectionDetailDrawer, {
		destroyOnClose: true,
		props: {
			collectionId: field.value!.oppositeCollectionId,
			formMode: 'view',
			itemId: itemKey.value,
			title: t('common.view_item'),
			fieldOverrides: fieldOverrides.value,
		},
	});
	modal.open();
}
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DFieldGroup>
			<DTooltip :disabled="disabled || permissions.canConnect.value" :text="t('permissions.no_connect_access')">
				<DButton
					class="
						h-10 min-w-0 flex-1 pe-1 text-start font-normal text-default ring-default
						hover:bg-default
						focus-visible:ring-primary
						disabled:ring-default!
						interactable-hover:ring-accented
					"
					color="neutral"
					variant="outline"
					data-testid="select"
					:disabled="disabled || !permissions.canConnect.value"
					:readonly
					:aria-readonly="readonly"
					@click="handleSelect"
				>
					<div class="min-w-0 flex-1">
						<Transition
							enter-active-class="transition-opacity duration-50"
							enter-from-class="opacity-0"
							enter-to-class="opacity-100"
							leave-active-class="transition-opacity duration-50"
							leave-from-class="opacity-100"
							leave-to-class="opacity-0"
							mode="out-in"
						>
							<div v-if="isLoading">
								<DSkeleton class="h-4 w-64 rounded-sm opacity-0" />
							</div>
							<span v-else-if="!item" class="font-normal text-dimmed select-none">
								{{ t('common.select_item') }}
							</span>
							<RenderCollectionDisplayTemplate
								v-else
								class="truncate text-sm"
								:item
								:collection-id="field!.oppositeCollectionId"
								:read="CollectionReads.Many"
							/>
						</Transition>
					</div>
					<M2ODeselectButton
						v-if="item && !readonly && showRemove"
						:disabled="disabled || !permissions.canDisconnect.value"
						@deselect="handleDeselect"
					/>
				</DButton>
			</DTooltip>

			<DTooltip v-if="!item && showCreate" :disabled="disabled || !permissions.canCreate.value" :text="t('permissions.no_create_access')">
				<DButton
					class="flex w-10 justify-center ring-default disabled:ring-default!"
					color="neutral"
					variant="outline"
					data-testid="create"
					:icon="appConfig.ui.icons.plus"
					:disabled="disabled || !permissions.canCreate.value"
					:ui="{ leadingIcon: 'size-5' }"
					@click="handleCreate"
				/>
			</DTooltip>
			<!-- Edit button when user has edit permission -->
			<DButton
				v-else-if="item && showEdit && canEditItem"
				class="flex w-10 justify-center ring-default"
				color="neutral"
				variant="outline"
				data-testid="edit"
				:icon="appConfig.ui.icons.edit"
				:ui="{ leadingIcon: 'size-5' }"
				@click="handleEdit"
			/>
			<!-- View button when user can view but not edit -->
			<DButton
				v-else-if="item && showEdit && !canEditItem && itemKey"
				class="flex w-10 justify-center ring-default"
				color="neutral"
				variant="outline"
				data-testid="view"
				icon="lucide:eye"
				:ui="{ leadingIcon: 'size-5' }"
				@click="handleView"
			/>
		</DFieldGroup>
	</DTooltip>
</template>
